import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { mainPreDefinedRanges } from '@app/dashboard/utils/dateRangePicker';
import { useTranslation } from 'react-i18next';

import { SkeletonBase } from '@unified-checkout/design-system';

import type { IActiveBalance } from '@app/dashboard/types/innerLayout/balance_and_transfers/balance';
import type { TAppliedRanges } from '@app/dashboard/types/innerLayout/date-ranges';
import type { ICardsTabs } from '@app/dashboard/types/innerLayout/balance_and_transfers';
import { useCancelQuery } from '@app/dashboard/hooks/useCancelQuery';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { getTransfersCardsTabs } from '@app/dashboard/services/inner_layout/balance_and_transfers/transfers';
import { formatToISODate } from '@app/dashboard/utils/dateFormats';

import { useActiveBalance } from '../../hooks/useManageAccount';

import ManageAccounts from '../../components/ManageAccounts';
import Header from './components/Header';
import AnalyticCards from './components/AnalyticCards';
import TableListing from './components/TableListing';

import styles from './transfers.module.scss';

const Transfers = () => {
  const [isTransfersLoading, setIsTransfersLoading] = useState(false);
  const [appliedRanges, setAppliedRanges] = useState<TAppliedRanges>({});
  const [activeBalance, setActiveBalance] = useState<IActiveBalance | null>(
    null
  );

  const {
    onChangeActiveBalance,
    haveAccountChanged,
    haveCurrencyChanged,
    queryResults,
  } = useActiveBalance({
    activeBalance,
    setActiveBalance,
  });

  const accountId = haveAccountChanged ?? activeBalance?.account?.id;

  const {
    data: currencies,
    isLoading: isBankAccountDetailsLoading,
    isFetching: isBankAccountDetailsFetching,
    isError: isBankAccountDetailsError,
  } = queryResults || {};

  const {
    data: summaryData,
    isFetching: isSummaryDataLoading,
    isError: isSummaryDataError,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_TRANSFERS_CARDS_TABS,
      appliedRanges,
      accountId,
      haveCurrencyChanged,
    ],
    queryFn: ({ signal }) =>
      getTransfersCardsTabs({
        appliedRanges: {
          start_date: formatToISODate(appliedRanges.start as Date),
          end_date: formatToISODate(appliedRanges.end as Date),
        },
        accountId: accountId,
        currency: (haveCurrencyChanged ?? activeBalance?.currency) as string,
        signal,
      }),
    select: (response) => response?.data?.data as ICardsTabs,
    enabled: !!accountId,
  });

  const handleRangesChange = (ranges: TAppliedRanges) => {
    setAppliedRanges(ranges);
  };

  const onTransferLoadingChange = (isLoading: boolean) => {
    setIsTransfersLoading(isLoading);
  };

  useCancelQuery({
    queryKey: [
      QUERY_KEYS.GET_TRANSFERS_CARDS_TABS,
      appliedRanges,
      accountId,
      haveCurrencyChanged,
    ],
    deps: [appliedRanges, accountId, haveCurrencyChanged],
  });

  const isFetching =
    isBankAccountDetailsFetching || isSummaryDataLoading || isTransfersLoading;
  const isError = isBankAccountDetailsError || isSummaryDataError;

  return (
    <section className={styles['transfers']}>
      <Header
        appliedRanges={appliedRanges}
        handleRangesChange={handleRangesChange}
        isTransfersLoading={isFetching}
      />
      {isBankAccountDetailsLoading ? (
        <SkeletonBase width="100%" height="3rem" />
      ) : (
        !isBankAccountDetailsError && (
          <ManageAccounts
            isLoading={isFetching}
            currencies={currencies}
            activeBalance={activeBalance}
            onChangeActiveBalance={onChangeActiveBalance}
          />
        )
      )}
      <AnalyticCards
        summaryCards={summaryData?.summary_cards}
        isCardTabsLoading={isBankAccountDetailsFetching || isSummaryDataLoading}
        isCardTabsError={isError}
      />
      <TableListing
        appliedRanges={appliedRanges}
        isTransfersLoading={isTransfersLoading}
        onTransferLoadingChange={onTransferLoadingChange}
        shouldDisableTabs={isFetching || isError}
        tabsProps={summaryData?.status_tabs}
        activeBalance={activeBalance}
      />
    </section>
  );
};

export default Transfers;
